import { UploadOutlined } from "@ant-design/icons";
import { Button, Col, ConfigProvider, Form, Row, Tabs } from "antd";
import PillTabs from "components/generalComponents/pillTabs/PillTabs";
import StatCard from "components/generalComponents/statCard/StatCard";
import TableComponent from "components/generalComponents/tableComponent/TableComponent";
import { strings } from "utils/localizedStrings";

import { FilterComponent } from "components/generalComponents/filterComponents/FilterComponents";
import LoadingComponent from "components/generalComponents/loadingComponent/LoadingComponent";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { GeneralConstants } from "../constants/GeneralConstants";
import RevCurMonthIcon from "../icons/rev_cur_month.png";
import RevPrevMonthIcon from "../icons/rev_prev_month.png";
import RevTodayIcon from "../icons/rev_today.png";
import SaleCurMonthIcon from "../icons/sale_cur_month.png";
import SalePrevMonthIcon from "../icons/sale_prev_month.png";
import SaleTodayIcon from "../icons/sale_today.png";
import { GeneralRowType } from "../interfaces/IGenerals";
import GeneralViewModel from "../viewModel/GeneralViewModel";

const GeneralFeature = () => {
  const {
    transactionReportLoading,
    transactionDetailReportLoading,
    service,
    setService,
    range,
    setRange,
    currencyOptions,
    finalFilterOptions,
    partnerList,
    carrierList,
    storeOptions,
    staffOptions,
    selectedPartnerType,
    isStore,
    filterForm,
    productCache,
    processedStatCards,
    processedTableData,
    processedRevenueData,
    revenueColumns,
    volumeColumns,
    handleExport,
    resetFilters,
    onGeneralFilterValuesChange,
    handlePartnerChange,
    handleStoreChange,
    handleStaffChange,
  } = GeneralViewModel();

  let cachedCapabilities = productCache?.getCache(CacheKeyEnum.capabilities);

  const isHasGeneralKey = cachedCapabilities?.some(
    (item) => item?.key === CapabilityEnum.general,
  );
  const selectedStore = filterForm?.getFieldValue("store");
  const isAllStore = !selectedStore;
  const filters = GeneralConstants.filters(
    strings,
    partnerList,
    carrierList,
    currencyOptions,
    handlePartnerChange,
    storeOptions,
    staffOptions,
    selectedPartnerType,
    isStore,
    isAllStore,
    handleStoreChange,
    handleStaffChange,
  );

  return (
    <>
      <Form form={filterForm} onValuesChange={onGeneralFilterValuesChange}>
        <Row gutter={[8, 8]} className="bg-transparent p-4">
          <Col span={24}>
            <div className="flex flex-col gap-1 mb-1">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-900">
                {strings.General.Title}
              </h2>
              <p className="text-sm text-gray-500">{strings.General.Summary}</p>
            </div>
          </Col>

          <Col span={24}>
            <div className="flex lg:flex-row flex-col gap-3 items-center justify-between">
              <div className="w-full lg:w-[50%]">
                <PillTabs
                  value={service}
                  onChange={(val) => setService(String(val))}
                  options={finalFilterOptions.map((item) => ({
                    label:
                      strings.GlobalService[
                        item.code as keyof typeof strings.GlobalService
                      ],
                    value: item.code,
                  }))}
                />
              </div>
              <Row
                gutter={[5, 0]}
                justify={"end"}
                className="w-full lg:w-[50%]"
              >
                {FilterComponent(
                  filters,
                  filterForm,
                )}
                <Col span={24} lg={4}>
                  <div className="truncate text-white hidden lg:flex">
                    {"button"}
                  </div>
                  <Form.Item>
                    <ConfigProvider
                      theme={{
                        token: {
                          colorPrimary: "#909090",
                        },
                      }}
                    >
                      <Button
                        type="primary"
                        size="middle"
                        style={{ borderRadius: 5, width: "100%" }}
                        onClick={resetFilters}
                      >
                        {strings.GlobalLabels.Redo}
                      </Button>
                    </ConfigProvider>
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Col>

          {isHasGeneralKey ? (
            <Col span={24}>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <StatCard
                  title={`${strings.General.Revenue.Label} ${processedStatCards.todayRevenue.currentTime}`}
                  value={
                    transactionReportLoading ? (
                      <LoadingComponent color="black" />
                    ) : (
                      processedStatCards.todayRevenue.value
                    )
                  }
                  trend={
                    processedStatCards.todayRevenue.change &&
                    !transactionReportLoading
                      ? {
                          direction: processedStatCards.todayRevenue.trend,
                          text: `${processedStatCards.todayRevenue.change} ${strings.General.Revenue.CompareWith} ${processedStatCards.todayRevenue.previousTime}`,
                        }
                      : undefined
                  }
                  icon={
                    <img
                      src={RevTodayIcon}
                      alt="Revenue Today"
                      className="w-5 h-5"
                    />
                  }
                  iconContainerClassName="bg-green-50"
                  iconColorClassName="text-green-600"
                />
                <StatCard
                  title={`${strings.General.Revenue.Label} ${processedStatCards.monthRevenue.currentTime}`}
                  value={
                    transactionReportLoading ? (
                      <LoadingComponent color="black" />
                    ) : (
                      processedStatCards.monthRevenue.value
                    )
                  }
                  trend={
                    processedStatCards.monthRevenue.change &&
                    !transactionReportLoading
                      ? {
                          direction: processedStatCards.monthRevenue.trend,
                          text: `${processedStatCards.monthRevenue.change} ${strings.General.Revenue.CompareWith} ${processedStatCards.monthRevenue.previousTime}`,
                        }
                      : undefined
                  }
                  icon={
                    <img
                      src={RevCurMonthIcon}
                      alt="Revenue Current Month"
                      className="w-5 h-5"
                    />
                  }
                  iconContainerClassName="bg-blue-50"
                  iconColorClassName="text-blue-600"
                />
                <StatCard
                  title={`${strings.General.Revenue.Label} ${processedStatCards.previousMonthRevenue.currentTime}`}
                  value={
                    transactionReportLoading ? (
                      <LoadingComponent color="black" />
                    ) : (
                      processedStatCards.previousMonthRevenue.value
                    )
                  }
                  trend={
                    processedStatCards.previousMonthRevenue.change &&
                    !transactionReportLoading
                      ? {
                          direction:
                            processedStatCards.previousMonthRevenue.trend,
                          text: `${processedStatCards.previousMonthRevenue.change} ${strings.General.Revenue.CompareWith} ${processedStatCards.previousMonthRevenue.previousTime}`,
                        }
                      : undefined
                  }
                  icon={
                    <img
                      src={RevPrevMonthIcon}
                      alt="Revenue Previous Month"
                      className="w-5 h-5"
                    />
                  }
                  iconContainerClassName="bg-[#E2702A]/15"
                  iconColorClassName="text-[#E2702A]"
                />
                <StatCard
                  title={`${strings.General.Revenue.Transaction} ${processedStatCards.todayTransactions.currentTime}`}
                  value={
                    transactionReportLoading ? (
                      <LoadingComponent color="black" />
                    ) : (
                      processedStatCards.todayTransactions.value
                    )
                  }
                  trend={
                    processedStatCards.todayTransactions.change &&
                    !transactionReportLoading
                      ? {
                          direction: processedStatCards.todayTransactions.trend,
                          text: `${processedStatCards.todayTransactions.change} ${strings.General.Revenue.CompareWith} ${processedStatCards.todayTransactions.previousTime}`,
                        }
                      : undefined
                  }
                  icon={
                    <img
                      src={SaleTodayIcon}
                      alt="Transactions Today"
                      className="w-5 h-5"
                    />
                  }
                  iconContainerClassName="bg-purple-50"
                  iconColorClassName="text-purple-600"
                />
                <StatCard
                  title={`${strings.General.Revenue.Transaction} ${processedStatCards.monthTransactions.currentTime}`}
                  value={
                    transactionReportLoading ? (
                      <LoadingComponent color="black" />
                    ) : (
                      processedStatCards.monthTransactions.value
                    )
                  }
                  trend={
                    processedStatCards.monthTransactions.change &&
                    !transactionReportLoading
                      ? {
                          direction: processedStatCards.monthTransactions.trend,
                          text: `${processedStatCards.monthTransactions.change} ${strings.General.Revenue.CompareWith} ${processedStatCards.monthTransactions.previousTime}`,
                        }
                      : undefined
                  }
                  icon={
                    <img
                      src={SaleCurMonthIcon}
                      alt="Transactions Current Month"
                      className="w-5 h-5"
                    />
                  }
                  iconContainerClassName="bg-rose-50"
                  iconColorClassName="text-rose-600"
                />
                <StatCard
                  title={`${strings.General.Revenue.Transaction} ${processedStatCards.previousMonthTransactions.currentTime}`}
                  value={
                    transactionReportLoading ? (
                      <LoadingComponent color="black" />
                    ) : (
                      processedStatCards.previousMonthTransactions.value
                    )
                  }
                  trend={
                    processedStatCards.previousMonthTransactions.change &&
                    !transactionReportLoading
                      ? {
                          direction:
                            processedStatCards.previousMonthTransactions.trend,
                          text: `${processedStatCards.previousMonthTransactions.change} ${strings.General.Revenue.CompareWith} ${processedStatCards.previousMonthTransactions.previousTime}`,
                        }
                      : undefined
                  }
                  icon={
                    <img
                      src={SalePrevMonthIcon}
                      alt="Transactions Previous Month"
                      className="w-5 h-5"
                    />
                  }
                  iconContainerClassName="bg-[#10DDDD]/15"
                  iconColorClassName="text-[#10DDDD]"
                />
              </div>
            </Col>
          ) : null}

          <Col span={service === "ALL" ? 0 : 24}>
            <div className="flex items-center justify-between gap-6 mt-2 mb-2 xl:overflow-x-auto overflow-x-scroll">
              <button
                onClick={() => setRange("day")}
                className={`min-w-[260px] h-10 rounded-md px-6 font-medium transition-colors ${
                  range === "day"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 border border-gray-300"
                }`}
              >
                {strings.General.ByDate}
              </button>
              <button
                onClick={() => setRange("week")}
                className={`min-w-[260px] h-10 rounded-md px-6 font-medium transition-colors ${
                  range === "week"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 border border-gray-300"
                }`}
              >
                {strings.General.ByWeek}
              </button>
              <button
                onClick={() => setRange("month")}
                className={`min-w-[260px] h-10 rounded-md px-6 font-medium transition-colors ${
                  range === "month"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 border border-gray-300"
                }`}
              >
                {strings.General.ByMonth}
              </button>
              <Button
                type="primary"
                className="h-10 rounded-md flex-1"
                style={{ backgroundColor: "#16a34a" }}
                icon={<UploadOutlined />}
                onClick={handleExport}
              >
                {strings.GlobalLabels.ExcelExporting}
              </Button>
            </div>
          </Col>

          <Col span={service === "ALL" ? 0 : 24}>
            <div className="bg-white rounded-lg shadow p-4">
              <Tabs
                defaultActiveKey="revenue"
                centered
                items={[
                  {
                    key: "revenue",
                    label: strings.General.Revenue.Label,
                    children: (
                      <TableComponent<GeneralRowType>
                        data={{
                          columns: revenueColumns,
                          dataSource: processedRevenueData,
                          loading: transactionDetailReportLoading,
                          loadingTitle: strings.GlobalLabels.Loading,
                          page: 0,
                          pageSize: processedRevenueData.length || 10,
                          total: processedRevenueData.length,
                          totalTitle: strings.GlobalLabels.Total,
                          scroll: { x: null, y: 600 },
                        }}
                      />
                    ),
                  },
                  {
                    key: "volume",
                    label: strings.General.Revenue.Transaction,
                    children: (
                      <TableComponent<GeneralRowType>
                        data={{
                          columns: volumeColumns,
                          dataSource: processedTableData,
                          loading: transactionDetailReportLoading,
                          loadingTitle: strings.GlobalLabels.Loading,
                          page: 0,
                          pageSize: processedTableData.length || 10,
                          total: processedTableData.length,
                          totalTitle: strings.GlobalLabels.Total,
                          scroll: { x: null, y: 600 },
                        }}
                      />
                    ),
                  },
                ]}
              />
            </div>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default GeneralFeature;
